import InputHidden from 'components/Form/InputHidden';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import TableStatic from 'components/Table/TableStatic';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {FormFields} from 'types/utils';
import {boolToIconString} from 'utils/functions';
import {onApiFormError} from 'utils/request/result';

import {
    ProfileNotificationFormFields,
    usePostProfileNotification,
} from '../services';
import {notificationTable} from '../styles.module.css';
import {Partner} from '../types';

type PartnerProps = {
    className?: string;
    myActivityDisabledSettingsSet: Set<string>;
    partner: Partner;
    teamActivityDisabledSettingsSet: Set<string>;
};

type ProfileNotificationFormEditNotificationSettingsData =
    FormFields<ProfileNotificationFormFields>;

type ProfileNotificationFormManageEmailsData =
    FormFields<ProfileNotificationFormFields>;

const PartnerTable: FC<PartnerProps> = ({
    className,
    myActivityDisabledSettingsSet,
    partner,
    teamActivityDisabledSettingsSet,
}) => {
    const [methodsManageEmails, methodEditNotificationSettings] = [
        useForm<ProfileNotificationFormManageEmailsData>(),
        useForm<ProfileNotificationFormEditNotificationSettingsData>(),
    ];

    const [
        {
            clearErrors: clearErrorsManageEmails,
            handleSubmit: handleSubmitManageEmails,
        },
        {
            clearErrors: clearErrorsEditNotificationSettings,
            handleSubmit: handleSubmitEditNotificationSettings,
        },
    ] = [methodsManageEmails, methodEditNotificationSettings];

    const {mutateAsync} = usePostProfileNotification();

    const onSubmitManageEmails = async (
        formData: ProfileNotificationFormManageEmailsData,
    ) => {
        clearErrorsManageEmails();
        await mutateAsync(formData).catch(onApiFormError(methodsManageEmails));
    };

    const onSubmitEditNotificationSettings = async (
        formData: ProfileNotificationFormEditNotificationSettingsData,
    ) => {
        clearErrorsEditNotificationSettings();
        await mutateAsync(formData).catch(
            onApiFormError(methodEditNotificationSettings),
        );
    };

    return (
        <TableStatic
            className={twMerge(
                notificationTable,
                className,
                'mt-6 w-full min-w-[720px]',
            )}
            hasStripes={true}
            id="member-table"
        >
            <TableStatic.Header>
                <TableStatic.HeaderRow>
                    <TableStatic.Column
                        className="whitespace-normal"
                        colSpan={2}
                    >
                        {partner.name} - {partner.contactEmail}
                    </TableStatic.Column>
                    <TableStatic.Column className="flex justify-end gap-2">
                        <FormProvider {...methodsManageEmails}>
                            <form
                                onSubmit={handleSubmitManageEmails(
                                    onSubmitManageEmails,
                                )}
                            >
                                <InputHidden
                                    name="partnerMid"
                                    value={partner.mid}
                                />
                                <InputHidden
                                    name="nextPage"
                                    value={window.Urls.email_settings_view()}
                                />
                                <SubmitButton kind="secondary" size="xs">
                                    {t`Manage Emails`}
                                </SubmitButton>
                            </form>
                        </FormProvider>
                        <FormProvider {...methodEditNotificationSettings}>
                            <form
                                onSubmit={handleSubmitEditNotificationSettings(
                                    onSubmitEditNotificationSettings,
                                )}
                            >
                                <InputHidden
                                    name="partnerMid"
                                    value={partner.mid}
                                />
                                <InputHidden
                                    name="nextPage"
                                    value={`${window.Urls.team_member_notifications_edit_react_view(
                                        partner.employeeMid,
                                    )}?from=notifications`}
                                />
                                <SubmitButton kind="secondary" size="xs">
                                    {t`Edit Notification Settings`}
                                </SubmitButton>
                            </form>
                        </FormProvider>
                    </TableStatic.Column>
                </TableStatic.HeaderRow>
            </TableStatic.Header>
            <TableStatic.Body>
                <TableStatic.Row>
                    <TableStatic.Cell />
                    <TableStatic.Cell className="whitespace-nowrap text-center">
                        {t`I../My..`}
                    </TableStatic.Cell>
                    <TableStatic.Cell className="whitespace-nowrap text-center">
                        {t`My team../My Team's..`}
                    </TableStatic.Cell>
                </TableStatic.Row>
                {partner.settings.map((setting) => (
                    <TableStatic.Row key={setting.displayName}>
                        <TableStatic.Cell>
                            {setting.displayName}
                        </TableStatic.Cell>
                        <TableStatic.Cell>
                            {setting.isEmailMyActivityEnabled !== undefined &&
                                !myActivityDisabledSettingsSet.has(
                                    setting.name,
                                ) && (
                                    <Icon
                                        icon={boolToIconString(
                                            setting.isEmailMyActivityEnabled,
                                        )}
                                        size="base"
                                    />
                                )}
                        </TableStatic.Cell>
                        <TableStatic.Cell>
                            {setting.isEmailTeamActivityEnabled !== undefined &&
                                !teamActivityDisabledSettingsSet.has(
                                    setting.name,
                                ) && (
                                    <Icon
                                        icon={boolToIconString(
                                            setting.isEmailTeamActivityEnabled,
                                        )}
                                        size="base"
                                    />
                                )}
                        </TableStatic.Cell>
                    </TableStatic.Row>
                ))}
            </TableStatic.Body>
        </TableStatic>
    );
};

export default PartnerTable;
